'use client'

/**
 * Шаг 3 импорта — валидация (Intelligence Terminal §Cards, §Badges).
 */

import { ArrowRight, ArrowLeft, Warning, XCircle, CheckCircle } from '@phosphor-icons/react'
import type { ValidationSummary, DuplicateCandidate } from '../import-wizard.types'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

interface Props {
  validation: ValidationSummary | null
  duplicates: DuplicateCandidate[]
  onNext: () => void
  onBack: () => void
  loading: boolean
}

export function StepValidate({ validation, duplicates, onNext, onBack, loading }: Props) {
  const t = useT()
  if (!validation) return null
  const hasBlockingErrors = (validation.errorRows ?? 0) > 0

  const blockingErrors = Array.isArray(validation.blockingErrors) ? validation.blockingErrors : []
  const warnings = Array.isArray(validation.warnings) ? validation.warnings : []
  const dupes = Array.isArray(duplicates) ? duplicates : []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label={t('importWizard.validate.totalRows')} value={validation.totalRows ?? 0} />
        <StatTile label={t('importWizard.validate.validRows')} value={validation.validRows ?? 0} tone="low" />
        <StatTile label={t('importWizard.validate.errorRows')} value={validation.errorRows ?? 0} tone="high" />
        <StatTile label={t('importWizard.validate.warningRows')} value={validation.warningRows ?? 0} tone="medium" />
      </div>

      {blockingErrors.length > 0 && (
        <section>
          <h4 className="mb-2 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--risk-high)]">
            <XCircle size={12} />
            {t('importWizard.validate.blockingErrors')}
          </h4>
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {blockingErrors.slice(0, 10).map((err, i) => (
              <div
                key={i}
                className="rounded-[2px] border px-3 py-1.5 font-mono text-[11px] tabular-nums bg-[var(--risk-high-bg)] border-[var(--risk-high)] text-[var(--risk-high)]"
              >
                <span className="font-medium">{t('importWizard.validate.row', { index: err.rowIndex + 1 })}</span>{' '}
                {Array.isArray(err.errors) ? err.errors.map((e) => `${e.field}: ${e.message}`).join('; ') : ''}
              </div>
            ))}
          </div>
        </section>
      )}

      {warnings.length > 0 && (
        <section>
          <h4 className="mb-2 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--risk-medium)]">
            <Warning size={12} />
            {t('importWizard.validate.warnings')}
          </h4>
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {warnings.slice(0, 10).map((w, i) => (
              <div
                key={i}
                className="rounded-[2px] border px-3 py-1.5 font-mono text-[11px] tabular-nums bg-[var(--risk-medium-bg)] border-[var(--risk-medium)] text-[var(--risk-medium)]"
              >
                <span className="font-medium">{t('importWizard.validate.row', { index: w.rowIndex + 1 })}</span>{' '}
                {Array.isArray(w.warnings) ? w.warnings.join('; ') : ''}
              </div>
            ))}
          </div>
        </section>
      )}

      {dupes.length > 0 && (
        <section>
          <h4 className="text-[13px] font-medium text-[var(--text-primary)]">
            {t('importWizard.validate.duplicatesFound')}{' '}
            <span className="font-mono tabular-nums">{dupes.length}</span>
          </h4>
          <p className="mt-0.5 text-[11px] text-[var(--text-tertiary)]">
            {t('importWizard.validate.duplicatesSkipped')}
          </p>
        </section>
      )}

      {!hasBlockingErrors && (
        <div className="flex items-center gap-2 rounded-[4px] border px-3 py-2 text-[13px] bg-[var(--risk-low-bg)] border-[var(--risk-low)] text-[var(--risk-low)]">
          <CheckCircle size={14} weight="fill" />
          <span>{t('importWizard.validate.ready')}</span>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-[var(--border-subtle)] pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-[4px] border border-[var(--border-default)] bg-transparent px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)] transition-colors duration-150 hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft size={12} />
          {t('common.action.back')}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={hasBlockingErrors || loading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[12px] font-medium',
            'bg-[var(--accent-data)] text-[var(--text-on-risk)]',
            'transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-data)] focus-visible:ring-offset-2',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          {loading ? t('common.state.loading') : t('importWizard.validate.preview')}
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  )
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'low' | 'medium' | 'high'
}) {
  const colorClass =
    tone === 'low'
      ? 'text-[var(--risk-low)]'
      : tone === 'medium'
        ? 'text-[var(--risk-medium)]'
        : tone === 'high'
          ? 'text-[var(--risk-high)]'
          : 'text-[var(--text-primary)]'
  return (
    <div className="rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3 text-center">
      <p className={cn('font-mono text-[22px] font-medium leading-none tabular-nums', colorClass)}>
        {value}
      </p>
      <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--text-tertiary)]">
        {label}
      </p>
    </div>
  )
}
